import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Box,
  CircularProgress,
  MenuItem,
  Tab,
  Tabs,
  TextField,
  Typography,
  makeStyles,
} from "@material-ui/core";
import {
  DialogContext,
  DialogOptions,
} from "../../../../components/src/DialogContext";
import ConfirmDialog from "./ConfirmDialog.web";
import KanbanBoard, { DragHandle, KanbanColumn, KanbanMove } from "./KanbanBoard.web";
import QuestionBankCard from "./QuestionBankCard.web";
import AddQuestionForm from "./AddQuestionForm.web";
import {
  BANK_COLUMN_KEY,
  BankPayload,
  BankQuestion,
  BankView,
  FLOW_LABELS,
  FlowFilter,
  MovePlan,
  buildBoard,
  customInScreen,
  defaultsInScreen,
  matchesFlow,
  moveAmongVisible,
  planCardMove,
} from "./questionBank";
import {
  addBankQuestion,
  fetchQuestionBank,
  removeBankQuestion,
  reorderBankQuestions,
  updateBankQuestion,
} from "./questionBankApi";

const useStyles = makeStyles((theme) => ({
  page: {
    padding: theme.spacing(3),
  },
  subtitle: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
  },
  note: {
    backgroundColor: "#EEF4FF",
    borderRadius: 8,
    color: "#2B4C8C",
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
  },
  toolbar: {
    alignItems: "center",
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(2),
    justifyContent: "space-between",
    marginBottom: theme.spacing(2),
  },
  flowFilter: {
    minWidth: 160,
  },
  summary: {
    color: theme.palette.text.secondary,
    display: "block",
    marginBottom: theme.spacing(1),
  },
  bankHint: {
    color: theme.palette.text.secondary,
    display: "block",
    marginTop: theme.spacing(0.5),
  },
  status: {
    alignItems: "center",
    display: "flex",
    gap: theme.spacing(2),
    padding: theme.spacing(3, 0),
  },
}));

const VIEW_TABS: Array<{ value: BankView; label: string }> = [
  { value: "custom", label: "Custom Questions" },
  { value: "default", label: "Default Questions" },
];

const FLOW_FILTERS: Array<{ value: FlowFilter; label: string }> = [
  { value: "all", label: "All flows" },
  { value: "business", label: FLOW_LABELS.business },
  { value: "personal", label: FLOW_LABELS.personal },
];

const confirmDialogOptions = (message: string): DialogOptions => ({
  title: "",
  dataToPass: { message, confirmLabel: "Delete" },
  renderedComponent: (ConfirmDialog as unknown) as React.ReactNode,
  width: "32%",
  withCustomDialog: false,
});

const ClientFacingQuestions = () => {
  const classes = useStyles();
  const openDialogBox = useContext(DialogContext) as (
    options: DialogOptions
  ) => Promise<unknown>;
  const [view, setView] = useState<BankView>("custom");
  const [flow, setFlow] = useState<FlowFilter>("all");
  const [payload, setPayload] = useState<BankPayload | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [busy, setBusy] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const busyRef = useRef<boolean>(false);

  const load = useCallback(async (nextView: BankView) => {
    setLoading(true);
    setError("");
    try {
      setPayload(await fetchQuestionBank(nextView));
    } catch (requestError) {
      setPayload(null);
      setError("We could not load the questions. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(view);
  }, [load, view]);

  // The ref blocks a second click or drop that lands before React re-renders the disabled controls.
  const run = async (action: () => Promise<BankPayload | void>) => {
    if (busyRef.current) {
      return;
    }
    busyRef.current = true;
    setBusy(true);
    setError("");
    try {
      const result = await action();
      setPayload(result ? result : await fetchQuestionBank("custom"));
    } catch (requestError) {
      setError("We could not save your change. Please try again.");
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  const questions = payload ? payload.questions : [];
  const screens = payload ? payload.screens : [];
  const showBank = view === "custom";
  const columns = useMemo(() => buildBoard(payload, flow, showBank), [payload, flow, showBank]);
  const inBankCount = columns
    .filter((column) => column.key === BANK_COLUMN_KEY)
    .reduce((total, column) => total + column.cards.length, 0);
  const inMagicLinkCount = columns
    .filter((column) => column.key !== BANK_COLUMN_KEY)
    .reduce((total, column) => total + column.cards.length, 0);

  const executePlan = (plan: MovePlan) => {
    if (plan.type === "reorder") {
      run(() => reorderBankQuestions(plan.keys));
    } else if (plan.type === "place") {
      run(async () => {
        await updateBankQuestion(plan.key, { screen: plan.screen, active: true });
        return reorderBankQuestions(plan.keys);
      });
    } else if (plan.type === "hide") {
      run(async () => {
        await updateBankQuestion(plan.key, { active: false });
      });
    }
  };

  const handleMoveCard = (move: KanbanMove) => {
    executePlan(
      planCardMove(questions, flow, {
        key: move.cardKey,
        fromColumn: move.fromColumn,
        fromIndex: move.fromIndex,
        toColumn: move.toColumn,
        toIndex: move.toIndex,
      })
    );
  };

  const customKeysOf = (screen: string) => {
    const custom = customInScreen(questions, screen);
    return {
      allKeys: custom.map((question) => question.key),
      visibleKeys: custom
        .filter((question) => matchesFlow(question, flow))
        .map((question) => question.key),
    };
  };

  const moveWithinScreen = (question: BankQuestion, direction: -1 | 1) => {
    const { allKeys, visibleKeys } = customKeysOf(question.screen);
    const keys = moveAmongVisible(allKeys, visibleKeys, question.key, direction);
    if (keys) {
      run(() => reorderBankQuestions(keys));
    }
  };

  const removeQuestion = async (question: BankQuestion) => {
    try {
      const confirmed = await openDialogBox(
        confirmDialogOptions(`Delete "${question.text}"? This can't be undone.`)
      );
      if (!confirmed) {
        return;
      }
    } catch (cancelled) {
      return;
    }
    run(async () => {
      await removeBankQuestion(question.key);
    });
  };

  const moveToScreen = (question: BankQuestion, screen: string, active: boolean) => {
    if (screen === question.screen && question.active === active) {
      return;
    }
    run(async () => {
      await updateBankQuestion(question.key, { active, screen });
    });
  };

  const renderCard = (
    question: BankQuestion,
    column: KanbanColumn<BankQuestion>,
    dragHandle: DragHandle
  ) => {
    const inBank = column.key === BANK_COLUMN_KEY;
    const defaultsShown = inBank
      ? 0
      : defaultsInScreen(questions, column.key).filter((card) => matchesFlow(card, flow)).length;
    const customIndex = column.cards.indexOf(question) - defaultsShown;
    const customCount = column.cards.length - defaultsShown;

    return (
      <QuestionBankCard
        busy={busy}
        canMoveDown={!inBank && customIndex < customCount - 1}
        canMoveUp={!inBank && customIndex > 0}
        dragHandle={dragHandle}
        inBank={inBank}
        onAddFromBank={(screen) => moveToScreen(question, screen, true)}
        onMoveDown={() => moveWithinScreen(question, 1)}
        onMoveToScreen={(screen) => moveToScreen(question, screen, true)}
        onMoveUp={() => moveWithinScreen(question, -1)}
        onRemove={() => removeQuestion(question)}
        onSaveText={(text) =>
          run(async () => {
            await updateBankQuestion(question.key, { text });
          })
        }
        onSendToBank={() =>
          run(async () => {
            await updateBankQuestion(question.key, { active: false });
          })
        }
        question={question}
        screens={screens}
      />
    );
  };

  const renderColumnHeader = (column: KanbanColumn<BankQuestion>) =>
    column.key === BANK_COLUMN_KEY ? (
      <Typography className={classes.bankHint} variant="caption">
        Your questions that are not being asked right now. Drag one into a category to include it
        in your Magic Link.
      </Typography>
    ) : null;

  const renderColumnFooter = (column: KanbanColumn<BankQuestion>) => {
    if (!showBank || column.key === BANK_COLUMN_KEY) {
      return null;
    }
    const firstFlow = column.cards.length > 0 ? column.cards[0].flow : "business";
    return (
      <AddQuestionForm
        busy={busy}
        defaultFlow={flow === "all" ? firstFlow : flow}
        onAdd={(text, newFlow, answerType, answerOptions) =>
          run(async () => {
            await addBankQuestion({
              screen: column.key,
              flow: newFlow,
              text,
              answer_type: answerType,
              answer_options: answerOptions,
            });
          })
        }
      />
    );
  };

  return (
    <Box className={classes.page} data-test-id="client-facing-questions">
      <Typography variant="h5">Your Client Facing Questions</Typography>
      <Typography className={classes.subtitle} variant="body2">
        Customize the questions that appear in your Magic Link to better understand client
        needs.
      </Typography>

      <Box className={classes.toolbar}>
        <Tabs
          indicatorColor="primary"
          onChange={(_event, value: BankView) => setView(value)}
          textColor="primary"
          value={view}
        >
          {VIEW_TABS.map((tab) => (
            <Tab
              data-test-id={`view-${tab.value}`}
              key={tab.value}
              label={tab.label}
              value={tab.value}
            />
          ))}
        </Tabs>
        <TextField
          className={classes.flowFilter}
          label="Flow"
          onChange={(event) => setFlow(event.target.value as FlowFilter)}
          select
          size="small"
          value={flow}
          variant="outlined"
        >
          {FLOW_FILTERS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      <Typography className={classes.note} component="div" variant="body2">
        {showBank
          ? "The questions marked as required feed the Magic Link pricing, so they can't be changed. Add your own questions to a category, and drag them between categories or into the Question Bank."
          : "The default questions, as SmartPath provides them. Switch to Custom Questions to add your own."}
      </Typography>

      {loading && (
        <Box className={classes.status}>
          <CircularProgress size={24} />
          <Typography variant="body2">Loading questions...</Typography>
        </Box>
      )}

      {error !== "" && (
        <Typography className={classes.status} color="error" variant="body2">
          {error}
        </Typography>
      )}

      {!loading && payload && (
        <>
          <Typography className={classes.summary} variant="caption">
            {showBank
              ? `${inMagicLinkCount} questions in your Magic Link, ${inBankCount} in the Question Bank`
              : `${inMagicLinkCount} default questions`}
          </Typography>
          <KanbanBoard
            columns={columns}
            dragDisabled={busy}
            emptyMessage="No questions to show."
            getCardKey={(card) => card.key}
            highlightedColumnKey={BANK_COLUMN_KEY}
            isCardDragDisabled={(card) => !card.editable}
            onMoveCard={showBank ? handleMoveCard : undefined}
            renderCard={renderCard}
            renderColumnFooter={renderColumnFooter}
            renderColumnHeader={renderColumnHeader}
          />
        </>
      )}
    </Box>
  );
};

export default ClientFacingQuestions;
